<template>
	<CardGrid tight>
		<Card :title="trans('events.selections.title')">
			<LabeledObject>
				<template #label>{{ trans('events.selections.types.title') }}</template>
				<select v-model="pack.selection_type">
					<option value="multiple">{{ trans('events.selections.types.multiple') }}</option>
					<option value="single">{{ trans('events.selections.types.single') }}</option>
				</select>
			</LabeledObject>
			<LabeledObject v-if="pack.selection_type === 'multiple'">
				<template #label>{{ trans('events.selections.limit') }}</template>
				<NumberInput v-model.number="pack.selection_limit" min="1"/>
			</LabeledObject>
			<Switch v-model.number="pack.selection_mandatory" label="events.selections.mandatory"/>
		</Card>
		<Card v-for="activity in activities" :key="`activity-${activity.id}`" :title="activity.title" :subtitle="activity.subtitle">
			<template #badges>
				<Badge v-if="activity.active" :color="Color.Green" active>{{ trans('general.enabled') }}</Badge>
				<Badge v-else :color="Color.Red">{{ trans('general.disabled') }}</Badge>
				<Badge class="cursor-pointer" @click.stop="copyTextToClipboard(activity.id)">#{{ activity.id }}</Badge>
			</template>
			<LabeledObject>
				<template #label>{{ trans('events.activities.maximum') }}</template>
				<p>{{ trans('events.activities.items.tickets_available', {number: `${activity.available}/${activity.maximum}`}) }}</p>
			</LabeledObject>
			<template #footer>
				<Link :as="SecondaryButton" :color="Color.Blue" :href="route('events.packs.activities.edit', {event: event.id, pack: pack.id, activity: activity.id})">
					<i class="fa-solid fa-edit mr-2"/>
					{{ trans('general.edit') }}
				</Link>
				<SoftButton :color="Color.Red" @click.stop="remove(activity)">
					<i class="fa-solid fa-trash mr-2"/>
					{{ trans('general.delete') }}
				</SoftButton>
			</template>
		</Card>
		<CreateActivity :event="event" :pack="pack"/>
	</CardGrid>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {type Event} from '@/Types/Events/Event'
import Badge from '@/Components/UI/Badges/Badge.vue'
import {type Pack} from '@/Types/Events/Packs/Pack'
import Switch from '@/Components/UI/Switches/Switch.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import {type Activity} from '@/Types/Events/Packs/Activities/Activity'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import CreateActivity from '@/Components/Events/Packs/Activities/CreateActivity.vue'
import {copyTextToClipboard, showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	pack:Pack,
	event:Event,
	activities:Array<Activity>,
}>()

const pack:Ref<Pack> = ref(props.pack)
const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const activities:Ref<Array<Activity>> = ref(props.activities)

function remove(activity:Activity):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.packs.activities.destroy', {
			event: event.value.id,
			pack: pack.value.id,
			activity: activity.id,
		})).then(() => {
			showSuccess(trans('general.deleted'))
			const index = activities.value.findIndex((a:Activity) => a.id === activity.id)
			if(index > -1){
				activities.value.splice(index, 1)
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>
